import {
  getCurrentTenant,
  getPageConfig,
  isLogin,
  subscribeTenantStateChange,
  toLogin,
} from '@/commons';
import { allMenus } from '@/menus/all';
import { Button, TabBar } from 'antd-mobile';
import {
  AppOutline,
  ClockCircleOutline,
  SearchOutline,
  UserOutline,
} from 'antd-mobile-icons';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import s from './index.module.less';
import { resolveMenuLabel } from './label';
import { resolveTenantBranding, type TenantBranding } from './tenant-branding';

const LAYOUT_BRANDING_EVENT = 'car-export-mobile:layout-branding-change';
let layoutBrandingOverride: TenantBranding | undefined;

const tabIcons = {
  me: <UserOutline />,
  search: <SearchOutline />,
  todo: <ClockCircleOutline />,
  workspace: <AppOutline />,
};

export type LayoutProps = {
  children: ReactNode;
};

export function setMobileLayoutBranding(branding?: TenantBranding) {
  layoutBrandingOverride = branding;
  window.dispatchEvent(new Event(LAYOUT_BRANDING_EVENT));
}

export function IcpFooter() {
  return (
    <footer className={s.footer}>
      <a
        className={s.filingLink}
        href="https://beian.miit.gov.cn/#/Integrated/index"
        rel="noopener noreferrer"
        target="_blank"
      >
        津ICP备2026008648号-1
      </a>
    </footer>
  );
}

export function Layout(props: LayoutProps) {
  const { children } = props;
  const pageConfig = getPageConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tenant, setTenant] = useState(() => getCurrentTenant());
  const [brandingOverride, setBrandingOverride] = useState(
    () => layoutBrandingOverride,
  );
  const title =
    typeof pageConfig.title === 'function'
      ? pageConfig.title({})
      : pageConfig.title;
  const defaultBranding = resolveTenantBranding({ pageTitle: title, tenant });
  const branding = brandingOverride || defaultBranding;

  useEffect(() => {
    return subscribeTenantStateChange(() => setTenant(getCurrentTenant()));
  }, []);
  useEffect(() => {
    const handleBrandingChange = () =>
      setBrandingOverride(layoutBrandingOverride);

    window.addEventListener(LAYOUT_BRANDING_EVENT, handleBrandingChange);
    handleBrandingChange();
    return () =>
      window.removeEventListener(LAYOUT_BRANDING_EVENT, handleBrandingChange);
  }, []);

  if (pageConfig.layout === false) return children;

  const navItems = allMenus.filter((item) => item.path && !item.hidden);
  const activeKey =
    navItems.find((item) => item.path === location.pathname)?.path || '/';

  return (
    <div className={s.root}>
      <header className={s.header}>
        <div>
          <h1 className={s.title}>{branding.name}</h1>
          {branding.description ? (
            <p className={s.description}>{branding.description}</p>
          ) : null}
        </div>
        <Button
          aria-label={isLogin() ? '个人中心' : '登录'}
          className={s.userButton}
          fill="none"
          onClick={() => {
            if (isLogin()) return;
            toLogin();
          }}
        >
          <UserOutline />
        </Button>
      </header>

      <main className={s.main}>{children}</main>

      <TabBar
        activeKey={activeKey}
        className={s.tabbar}
        onChange={(key) => navigate(key)}
        safeArea
      >
        {navItems.map((item) => {
          return (
            <TabBar.Item
              icon={
                tabIcons[item.icon as keyof typeof tabIcons] || <AppOutline />
              }
              key={item.path}
              title={resolveMenuLabel(item.label, t as (key: string) => string)}
            />
          );
        })}
      </TabBar>
    </div>
  );
}
